import React, { useEffect, useRef, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import { useStrategy } from "@/context/StrategyContext";
import { sapi, errText } from "@/lib/strategy";
import { PageHead, Avatar, DemoBadge } from "@/components/strategy/ui";

const SUGGESTED = [
  "What are the three fastest ways Growth could generate £10,000 MRR?",
  "Where do Andy and Jon disagree most?",
  "Which products could we sell to NicheCom customers immediately?",
  "What must we resolve before licensing Social Pro internationally?",
  "What would make Growth attractive to an investor in three years?",
  "Which opportunities help us become independent of the property sector?",
  "What should Jon prioritise next week?",
];

function renderMarkdown(text) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = esc(text).split("\n");
  let html = "", inList = null;
  const close = () => { if (inList) { html += `</${inList}>`; inList = null; } };
  for (const raw of lines) {
    const line = raw.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/`(.+?)`/g, "<code>$1</code>");
    if (/^\s*[-*•]\s+/.test(line)) { if (inList !== "ul") { close(); html += "<ul>"; inList = "ul"; } html += `<li>${line.replace(/^\s*[-*•]\s+/, "")}</li>`; }
    else if (/^\s*\d+[.)]\s+/.test(line)) { if (inList !== "ol") { close(); html += "<ol>"; inList = "ol"; } html += `<li>${line.replace(/^\s*\d+[.)]\s+/, "")}</li>`; }
    else if (/^#{1,3}\s/.test(line)) { close(); html += `<h3>${line.replace(/^#{1,3}\s/, "")}</h3>`; }
    else if (line.trim() === "") { close(); }
    else { close(); html += `<p>${line}</p>`; }
  }
  close();
  return html;
}

export default function Adviser() {
  const { round, roundId, me } = useStrategy();
  const key = `st_adv_${roundId}`;
  const [sid] = useState(() => { const s = localStorage.getItem(key) || `s_${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem(key, s); return s; });
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { if (roundId) sapi.get(`/rounds/${roundId}/adviser/${sid}`).then((r) => setMsgs(r.data)).catch(() => {}); }, [roundId, sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const ask = async (q) => {
    const message = (q || text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { id: `tmp_${Date.now()}`, role: "user", text: message, participant_id: me.id }]);
    try {
      const { data } = await sapi.post(`/rounds/${roundId}/adviser`, { message, session_id: sid });
      setMsgs((m) => [...m, { id: `a_${Date.now()}`, role: "assistant", text: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { id: `e_${Date.now()}`, role: "assistant", text: errText(e) }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col" style={{ minHeight: "calc(100vh - 140px)" }}>
      <PageHead eyebrow="AI strategy adviser" title="Ask the evidence" sub="Grounded in the completed assessment, recorded decisions, business assumptions, opportunity matrix, revenue model and roadmap. Recommendations are labelled as such — only decisions marked Agreed are treated as settled."
        actions={<>{round?.demo && <DemoBadge />}<span className="st-pill" style={{ background: "rgba(243,239,230,.06)", color: "var(--st-muted)" }}>Claude Sonnet 4.6</span></>} />
      <div className="st-panel flex-1 flex flex-col overflow-hidden" data-testid="adviser-panel">
        <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-5" style={{ maxHeight: "60vh" }}>
          {!msgs.length && (
            <div className="st-fade-in">
              <p className="st-caps mb-3">Try asking</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {SUGGESTED.map((s) => <button key={s} onClick={() => ask(s)} className="st-option !py-3 text-sm" data-testid="adviser-suggestion"><Sparkles size={14} style={{ color: "var(--st-lime)", marginTop: 3 }} /><span>{s}</span></button>)}
              </div>
            </div>
          )}
          {msgs.map((m) => (
            <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`adviser-msg-${m.role}`}>
              {m.role === "assistant" && <span className="grid place-items-center w-8 h-8 rounded-full shrink-0" style={{ background: "var(--st-lime-dim)" }}><Sparkles size={14} style={{ color: "var(--st-lime)" }} /></span>}
              <div className={`rounded-2xl px-4 py-3 text-sm max-w-[78%] ${m.role === "user" ? "" : "st-prose"}`} style={m.role === "user" ? { background: "var(--st-lime-dim)", border: "1px solid rgba(200,247,74,.25)" } : { background: "rgba(243,239,230,.04)", border: "1px solid var(--st-line)" }}>
                {m.role === "user" ? m.text : <div dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />}
              </div>
              {m.role === "user" && <Avatar pid={m.participant_id || me.id} size={32} />}
            </div>
          ))}
          {busy && <div className="flex gap-3 items-center st-muted text-sm"><span className="grid place-items-center w-8 h-8 rounded-full" style={{ background: "var(--st-lime-dim)" }}><Sparkles size={14} className="animate-pulse" style={{ color: "var(--st-lime)" }} /></span>Reading the assessment and model…</div>}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-4 border-t flex gap-2" style={{ borderColor: "var(--st-line)" }}>
          <input className="st-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask a strategic question…" data-testid="adviser-input" />
          <button type="submit" className="st-btn st-btn-primary" disabled={busy || !text.trim()} data-testid="adviser-send"><Send size={16} /></button>
        </form>
      </div>
    </div>
  );
}
